import "../account.css";
import "./accountBookings.css";
import { useState, useEffect, useContext } from "react";
import usersRequest from "../../../request/usersRequest";
import scheduleBasedRequest from "../../../request/scheduleBasedRequest";
import { CombinedContext } from "../../../context/combinedContext";
import translations from "../../../translations/translations";
import { useNavigate } from "react-router-dom";
import generalRequest from "../../../request/generalRequest";
import BookingModal from "../../bookingModal/bookingModal";
import { AlertCircle, Calendar, CalendarCheck, ChevronRight, Clock, History, MapPin, RefreshCw, Star, TriangleAlert, User, X } from "lucide-react";
import AccountBookingsSkeleton from "./accountBookingsSkeleton/accountBookingsSkeleton";
import useModalDismiss from "../../common/useModalDismiss";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import relativeTime from "dayjs/plugin/relativeTime";
import localizedFormat from "dayjs/plugin/localizedFormat";
import "dayjs/locale/es";

/* Appointments are read in the shop's zone and printed with a relative
   countdown, matching the booking detail page. Registered here rather than
   relying on whichever route happened to load first. */
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);
dayjs.extend(localizedFormat);

const DEFAULT_TZ = "America/Santo_Domingo";

const STATUS_TONE = {
	completed: "ok",
	confirmed: "info",
	pending: "warn",
	in_progress: "warn",
	rescheduled: "warn",
	cancelled: "danger",
	no_show: "neutral",
};

const AccountBookings = () => {
	const [bookings, setBookings] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [isBookingOpen, setIsBookingOpen] = useState(false);
	const [rescheduleData, setRescheduleData] = useState(null);
	const [shopData, setShopData] = useState(null);
	const [confirmCancelId, setConfirmCancelId] = useState(null);

	useModalDismiss(() => setConfirmCancelId(null), { enabled: Boolean(confirmCancelId) });

	const { userData, language } = useContext(CombinedContext);
	const navigate = useNavigate();

	const isLoggedIn = !!(userData && userData.cognitoTokens?.IdToken);
	const userId = userData?.user?.userId;

	const t = translations[language].account.bookings;
	const mb = translations[language].myBookings;

	useEffect(() => {
		async function loadBookings() {
			if (!isLoggedIn || !userId) {
				setLoading(false);
				return;
			}
			try {
				const data = await usersRequest.getUserBookings(userId, userData.cognitoTokens?.AccessToken);
				setBookings(data.bookings || []);
			} catch (err) {
				setError(mb.errors.load);
			} finally {
				setLoading(false);
			}
		}
		loadBookings();
	}, [isLoggedIn, userId, userData?.cognitoTokens?.IdToken]);

	/* ── Helpers ─────────────────────────────────────────────────── */
	const zoneOf = (b) => b.timeZone || DEFAULT_TZ;
	const at = (b, iso) => dayjs.tz(iso, zoneOf(b)).locale(language);
	const serviceName = (s) => (language === "es" ? s.serviceNameEs : s.serviceNameEn) || s.name || s.serviceNameEn || s.serviceNameEs;
	const serviceList = (b) => (Array.isArray(b.services) ? b.services.map(serviceName).filter(Boolean) : []);

	const StatusBadge = ({ status }) => (
		<span className={`ac-badge ac-badge--${STATUS_TONE[status] || "neutral"}`}>
			<i className="ac-badge-dot" />
			{t.status[status] || status}
		</span>
	);

	const now = new Date();
	const upcoming = bookings
		.filter((b) => new Date(b.startTime) > now && !["cancelled", "no_show"].includes(b.status))
		/* soonest first — the appointment you need to know about leads */
		.sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
	const history = bookings
		.filter((b) => new Date(b.startTime) <= now || ["cancelled", "no_show"].includes(b.status))
		/* most recent first */
		.sort((a, b) => new Date(b.startTime) - new Date(a.startTime));

	const [next, ...restUpcoming] = upcoming;

	const openBooking = (bookingId) => navigate(`/booking/${bookingId}`);

	async function handleCancel(bookingId) {
		try {
			await scheduleBasedRequest.cancelBooking(bookingId, userData.cognitoTokens?.IdToken);
			/* Mark it cancelled rather than dropping the row — the visit still
			   belongs in the customer's history. */
			setBookings((prev) => prev.map((b) => (b.bookingId === bookingId ? { ...b, status: "cancelled" } : b)));
		} catch (err) {
			setError(mb.errors.cancel);
		}
	}

	const reloadBookings = async () => {
		try {
			const data = await usersRequest.getUserBookings(userId, userData.cognitoTokens?.AccessToken);
			setBookings(data.bookings || []);
		} catch (err) {
			setError(mb.errors.reload);
		}
	};

	async function handleReschedule(booking) {
		try {
			const res = await generalRequest.getShopWithstaffsByIdPublic(booking.shopId);
			setShopData(res.shop);
			setRescheduleData(booking);
			setIsBookingOpen(true);
		} catch (err) {
			setError(mb.errors.reschedule);
		}
	}

	if (loading) {
		return <AccountBookingsSkeleton />;
	}

	/* Signed out: the lists would just render as two empty shells. */
	if (!isLoggedIn || !userId) {
		return (
			<div className="ac-page apbk">
				<header className="ac-head">
					<h1 className="ac-title">{t.title}</h1>
				</header>

				<div className="ac-empty">
					<span className="ac-icon">
						<Calendar size={18} />
					</span>
					<p>{t.signedOut}</p>
				</div>
			</div>
		);
	}

	const actionButtons = (b, size = "") => (
		<div className="apbk-actions" onClick={(e) => e.stopPropagation()}>
			<button className={`ac-btn ac-btn--ghost ${size}`} onClick={() => handleReschedule(b)}>
				<RefreshCw size={13} />
				{t.reschedule}
			</button>
			<button className={`ac-btn ac-btn--danger-ghost ${size}`} onClick={() => setConfirmCancelId(b.bookingId)}>
				<X size={13} />
				{t.cancel}
			</button>
		</div>
	);

	return (
		<div className="ac-page apbk">
			<header className="ac-head">
				<h1 className="ac-title">{t.title}</h1>
				<p className="ac-sub">{t.subtitle}</p>
			</header>

			{error && (
				<div className="ac-note is-danger">
					<AlertCircle size={14} />
					<span>{error}</span>
				</div>
			)}

			{/* ═══ NEXT APPOINTMENT — the one thing this page exists to answer ═══ */}
			{next && (
				<section className="apbk-next" onClick={() => openBooking(next.bookingId)}>
					<div className="apbk-next-main">
						<div className="apbk-next-date">
							<em>{at(next, next.startTime).format("MMM")}</em>
							<strong>{at(next, next.startTime).format("D")}</strong>
							<span>{at(next, next.startTime).format("ddd")}</span>
						</div>

						<div className="apbk-next-copy">
							<span className="apbk-next-eyebrow">{t.nextUp}</span>

							<span className="apbk-next-time">
								{at(next, next.startTime).format("h:mm A")}
								{next.endTime && <em> – {at(next, next.endTime).format("h:mm A")}</em>}
							</span>

							<span className="apbk-next-when">{dayjs(next.startTime).locale(language).fromNow()}</span>

							<span className="apbk-next-shop">
								<MapPin size={13} strokeWidth={1.9} />
								{next.shopName}
							</span>

							{next.staffName && (
								<span className="apbk-next-staff">
									<User size={13} strokeWidth={1.9} />
									{next.staffName}
								</span>
							)}

							{serviceList(next).length > 0 && (
								<span className="apbk-next-services">
									{serviceList(next).map((name, i) => (
										<span key={i} className="apbk-chip">
											{name}
										</span>
									))}
								</span>
							)}
						</div>

						<ChevronRight size={18} className="apbk-next-chevron" />
					</div>

					{actionButtons(next)}
				</section>
			)}

			{/* ═══ UPCOMING ═══ */}
			<section className="ac-card">
				<div className="ac-card-head">
					<span className="ac-icon">
						<CalendarCheck size={16} />
					</span>
					<h3>{mb.upcomingTitle}</h3>
					{upcoming.length > 0 && <span className="ac-badge ac-badge--info">{upcoming.length}</span>}
				</div>

				{restUpcoming.length === 0 ? (
					<div className="ac-card-body">
						<div className="ac-empty">
							<span className="ac-icon">
								<Calendar size={18} />
							</span>
							<p>{next ? t.noOtherUpcoming : t.noUpcoming}</p>
						</div>
					</div>
				) : (
					<div className="ac-card-body is-flush">
						{restUpcoming.map((b) => (
							<div key={b.bookingId} className="apbk-card" onClick={() => openBooking(b.bookingId)}>
								<span className="apbk-accent" />

								<div className="apbk-card-copy">
									<span className="apbk-shop">{b.shopName}</span>
									{serviceList(b).length > 0 && <span className="apbk-services">{serviceList(b).join(" · ")}</span>}

									<span className="apbk-when">
										<span className="apbk-chip">
											<Calendar size={11} />
											{at(b, b.startTime).format("ddd, D MMM")}
										</span>
										<span className="apbk-chip">
											<Clock size={11} />
											{at(b, b.startTime).format("h:mm A")}
										</span>
										{b.staffName && (
											<span className="apbk-chip">
												<User size={11} />
												{b.staffName}
											</span>
										)}
									</span>
								</div>

								{actionButtons(b, "ac-btn--sm")}
							</div>
						))}
					</div>
				)}
			</section>

			{/* ═══ HISTORY ═══ */}
			<section className="ac-card">
				<div className="ac-card-head">
					<span className="ac-icon">
						<History size={16} />
					</span>
					<h3>{mb.historyTitle}</h3>
					{history.length > 0 && <span className="ac-badge ac-badge--neutral">{history.length}</span>}
				</div>

				{history.length === 0 ? (
					<div className="ac-card-body">
						<div className="ac-empty">
							<span className="ac-icon">
								<History size={18} />
							</span>
							<p>{mb.noHistory}</p>
						</div>
					</div>
				) : (
					<div className="ac-card-body is-flush">
						{/* WIDE — table */}
						<div className="ac-table-wrap apbk-table">
							<table className="ac-table">
								<thead>
									<tr>
										<th>{mb.table.date}</th>
										<th>{mb.table.service}</th>
										<th>{mb.table.shop}</th>
										<th>{mb.table.status}</th>
										<th className="apbk-th-action" />
									</tr>
								</thead>
								<tbody>
									{history.map((h) => (
										<tr key={h.bookingId} className="is-clickable" onClick={() => openBooking(h.bookingId)}>
											<td>{at(h, h.startTime).format("D MMM YYYY")}</td>
											<td className="is-muted">{serviceList(h).join(", ") || "—"}</td>
											<td>{h.shopName || "—"}</td>
											<td>
												<StatusBadge status={h.status} />
											</td>
											<td className="apbk-td-action">
												{h.status === "completed" && (
													<button
														className="ac-linkbtn apbk-review-link"
														onClick={(e) => {
															e.stopPropagation();
															navigate(`/reviews/${h.bookingId}`);
														}}
													>
														<Star size={13} strokeWidth={2} />
														{t.review}
													</button>
												)}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>

						{/* NARROW — cards */}
						<div className="apbk-history-cards">
							{history.map((h) => (
								<div key={h.bookingId} className="apbk-history-card" onClick={() => openBooking(h.bookingId)}>
									<div className="apbk-history-top">
										<span className="apbk-history-date">{at(h, h.startTime).format("D MMM YYYY")}</span>
										<StatusBadge status={h.status} />
									</div>

									{serviceList(h).length > 0 && (
										<div className="apbk-history-services">
											{serviceList(h).map((name, i) => (
												<span key={i} className="apbk-chip">
													{name}
												</span>
											))}
										</div>
									)}

									<span className="apbk-history-shop">{h.shopName || "—"}</span>

									{h.status === "completed" && (
										<button
											className="ac-linkbtn apbk-review-link"
											onClick={(e) => {
												e.stopPropagation();
												navigate(`/reviews/${h.bookingId}`);
											}}
										>
											<Star size={13} strokeWidth={2} />
											{t.review}
										</button>
									)}
								</div>
							))}
						</div>
					</div>
				)}
			</section>

			{/* ── RESCHEDULE MODAL ── */}
			{isBookingOpen && shopData && rescheduleData && (
				<BookingModal
					open={isBookingOpen}
					onClose={() => {
						setIsBookingOpen(false);
						setRescheduleData(null);
						setShopData(null);
						reloadBookings();
					}}
					availabilityId={shopData.shopId}
					shopData={shopData}
					initialServices={rescheduleData.services}
					initialstaffId={rescheduleData.staffId}
					mode="reschedule"
					existingBooking={rescheduleData}
				/>
			)}

			{/* ── CANCEL CONFIRM ── */}
			{confirmCancelId && (
				<div className="apbk-overlay">
					<div className="apbk-modal">
						<span className="ac-icon is-danger apbk-modal-icon">
							<TriangleAlert size={20} />
						</span>

						<h3>{t.confirmCancelTitle}</h3>
						<p>{t.confirmCancelText}</p>

						<div className="apbk-modal-actions">
							<button className="ac-btn ac-btn--ghost" onClick={() => setConfirmCancelId(null)}>
								{t.confirmCancelNo}
							</button>
							<button
								className="ac-btn ac-btn--danger"
								onClick={async () => {
									await handleCancel(confirmCancelId);
									setConfirmCancelId(null);
								}}
							>
								{t.confirmCancelYes}
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default AccountBookings;
